// OTP verify (AUTH-001): 6-digit code with paste and one-time-code autofill (SC 3.3.8 accessible
// authentication: no puzzles), resend countdown from resend_after_s, then the MFA step when required.
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { normalizeDigits } from "@ady/format";
import { t } from "@ady/i18n";
import { Banner, Button, Field, focusFirstInvalid, Icon, Input, useToast } from "@ady/ui";
import { api } from "../api/client";
import { errorMessage } from "../api/errors";
import { keys } from "../api/queries";
import { useGo } from "../shell/AppLink";
import { TID } from "../testids";
import { AuthLayout } from "./AuthLayout";
import { LoginShowcase } from "./LoginShowcase";
import { nextPath } from "./SignIn";

export function VerifyPage() {
  const params = new URLSearchParams(location.search);
  const email = params.get("email") ?? "";
  const [challenge, setChallenge] = useState(params.get("challenge") ?? "");
  const [code, setCode] = useState("");
  const [fieldError, setFieldError] = useState<string>();
  const [formError, setFormError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(Number(params.get("resend") ?? 30));
  const formRef = useRef<HTMLFormElement>(null);
  const go = useGo();
  const qc = useQueryClient();
  const toast = useToast();

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendIn]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(undefined);
    if (!/^\d{6}$/.test(code)) {
      setFieldError(t("auth.codeInvalid"));
      requestAnimationFrame(() => focusFirstInvalid(formRef.current));
      return;
    }
    setFieldError(undefined);
    setBusy(true);
    try {
      const res = await api.otpVerify({ challenge_id: challenge, code });
      if (res.mfa_required) {
        go(`/sign-in/mfa?next=${encodeURIComponent(nextPath())}`);
        return;
      }
      await qc.invalidateQueries({ queryKey: keys.me });
      toast.show({ tone: "success", title: t("auth.signedIn") });
      go(nextPath());
    } catch (err) {
      setFieldError(errorMessage(err));
      requestAnimationFrame(() => focusFirstInvalid(formRef.current));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setFormError(undefined);
    try {
      const res = await api.otpStart({ channel: "email", value: email, purpose: "signin" });
      setChallenge(res.challenge_id);
      setResendIn(res.resend_after_s);
      toast.show({ tone: "info", title: t("auth.resent") });
    } catch (err) {
      setFormError(errorMessage(err));
    }
  };

  return (
    <AuthLayout
      title={t("auth.verifyTitle")}
      lead={t("auth.verifyLead", { email })}
      testId={TID.verifyPage}
      showcase={<LoginShowcase />}
    >
      {formError ? <Banner kind="error" title={formError} data-testid={TID.formError} /> : null}
      <form ref={formRef} onSubmit={submit} noValidate className="flex flex-col gap-4">
        <Field label={t("auth.codeLabel")} error={fieldError} errorTestId={TID.otpError} required>
          <Input
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            maxLength={6}
            value={code}
            onChange={(e) =>
              setCode(normalizeDigits(e.target.value).replace(/\D/g, "").slice(0, 6))
            }
            ledger
            className="tracking-[0.3em]"
            data-testid={TID.codeInput}
            autoFocus
          />
        </Field>
        <Button
          icon="check"
          type="submit"
          fullWidth
          loading={busy}
          loadingLabel={t("auth.verifying")}
          data-testid={TID.verifyButton}
        >
          {t("auth.verify")}
        </Button>
      </form>
      {/* Two quiet text links under the button, one each side: resend (greyed while counting down,
          the countdown is its label) and change the email. */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-sm">
        <button
          type="button"
          onClick={resend}
          disabled={resendIn > 0}
          data-testid={TID.resendButton}
          className="inline-flex min-h-control items-center gap-1.5 font-medium text-link hover:underline disabled:cursor-not-allowed disabled:text-fg-tertiary disabled:no-underline"
        >
          <Icon name="refresh" />
          {resendIn > 0 ? t("auth.resendIn", { seconds: resendIn }) : t("auth.resend")}
        </button>
        <button
          type="button"
          onClick={() => go("/sign-in")}
          className="inline-flex min-h-control items-center gap-1.5 font-medium text-link hover:underline"
        >
          <Icon name="mail" />
          {t("auth.changeEmail")}
        </button>
      </div>
    </AuthLayout>
  );
}
